import { Link } from 'react-router-dom'
import { useEvent } from '../../hooks/useEvent'
import type { RequestItem } from '../../hooks/useMyRequests'
import { terminalMillis } from '../../hooks/useMyRequests'
import { categoryLabel } from '../../lib/commonStrings'
import { formatRelativeTime } from '../../lib/formatDate'
import { cn, linkBrandLabel, scheduleSetPath } from '../../lib/utils'
import { setReportCategoryLabel } from '../../lib/setReportLabels'
import { eventReportCategoryLabel } from '../../lib/eventReportLabels'
import { getRegion } from '@shared/regions'
import { Skeleton } from '../../components/ui/skeleton'
import { KIND_ICON, coveredRegionId, requestDisplay, requestKindLabel } from './status'

type Props = { loading: true } | { loading?: false; item: RequestItem; locale: string }

const CARD = 'rounded-xl border border-border bg-card p-3 space-y-1.5'
const LINK = 'font-medium text-primary hover:underline'

// The event's name if it's readable, else `fallback`. Draft events are
// admin-only, so a suggestion still "in review" resolves to its URL text.
function EventLink({ eventId, to, fallback }: { eventId: string; to: string; fallback: string }) {
  const { event } = useEvent(eventId)
  if (!event) return <span className="font-medium">{fallback}</span>
  return <Link to={to} className={LINK}>{event.name}</Link>
}

function SuggestionText({ item }: { item: Extract<RequestItem, { kind: 'suggestion' }> }) {
  const { url, eventIds } = item.doc
  const brand = linkBrandLabel(url) ?? url
  if (eventIds?.length) {
    return (
      <p className="text-sm">
        {eventIds.map((id, i) => (
          <span key={id}>
            {i > 0 && ', '}
            <EventLink eventId={id} to={`/events/${id}`} fallback={brand} />
          </span>
        ))}
      </p>
    )
  }
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium">{brand}</p>
      <p className="truncate text-xs text-muted-foreground">{url}</p>
    </div>
  )
}

function LeadText({ item }: { item: Extract<RequestItem, { kind: 'lead' }> }) {
  const { eventId, url, status } = item.doc
  const to = status === 'seeded' ? `/events/${eventId}/schedule` : `/events/${eventId}`
  return (
    <div className="min-w-0">
      <p className="text-sm">
        <EventLink eventId={eventId} to={to} fallback={eventId} />
      </p>
      {url && <p className="truncate text-xs text-muted-foreground">{url}</p>}
    </div>
  )
}

function FeedbackText({ item }: { item: Extract<RequestItem, { kind: 'feedback' }> }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{categoryLabel(item.doc.category)}</p>
      <p className="text-sm line-clamp-2 break-words">{item.doc.message}</p>
    </div>
  )
}

function RegionText({ item }: { item: Extract<RequestItem, { kind: 'region' }> }) {
  const covered = getRegion(coveredRegionId(item))
  return (
    <p className="text-sm font-medium">
      {item.doc.text}
      {covered && <span className="font-normal text-muted-foreground"> · {covered.name}</span>}
    </p>
  )
}

function SetReportText({ item }: { item: Extract<RequestItem, { kind: 'setReport' }> }) {
  const { eventId, setId, category, message } = item.doc
  return (
    <div className="min-w-0">
      <p className="text-sm">
        <EventLink eventId={eventId} to={scheduleSetPath(eventId, setId)} fallback={eventId} />
        <span className="text-muted-foreground"> · {setReportCategoryLabel(category)}</span>
      </p>
      {message && <p className="text-xs text-muted-foreground line-clamp-2 break-words">{message}</p>}
    </div>
  )
}

function EventReportText({ item }: { item: Extract<RequestItem, { kind: 'eventReport' }> }) {
  const { eventId, category, message } = item.doc
  return (
    <div className="min-w-0">
      <p className="text-sm">
        <EventLink eventId={eventId} to={`/events/${eventId}`} fallback={eventId} />
        <span className="text-muted-foreground"> · {eventReportCategoryLabel(category)}</span>
      </p>
      {message && <p className="text-xs text-muted-foreground line-clamp-2 break-words">{message}</p>}
    </div>
  )
}

function PrimaryText({ item }: { item: RequestItem }) {
  switch (item.kind) {
    case 'suggestion':
      return <SuggestionText item={item} />
    case 'lead':
      return <LeadText item={item} />
    case 'feedback':
      return <FeedbackText item={item} />
    case 'region':
      return <RegionText item={item} />
    case 'setReport':
      return <SetReportText item={item} />
    case 'eventReport':
      return <EventReportText item={item} />
  }
}

export function RequestRow(props: Props) {
  if (props.loading) {
    return (
      <li className={CARD}>
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-20" />
      </li>
    )
  }

  const { item, locale } = props
  const KindIcon = KIND_ICON[item.kind]
  const { label, icon: StatusIcon, tone } = requestDisplay(item)
  // serverTimestamp() resolves a beat after the write lands, so a just-submitted
  // doc can arrive with createdAt 0.
  const submitted = item.createdAt ? formatRelativeTime(new Date(item.createdAt), locale) : null
  const resolvedAt = terminalMillis(item)
  const resolved =
    resolvedAt && resolvedAt !== item.createdAt ? formatRelativeTime(new Date(resolvedAt), locale) : null

  return (
    <li className={CARD} data-testid={`request-row-${item.kind}`}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <KindIcon className="size-3.5 shrink-0" />
        <span>{requestKindLabel(item.kind)}</span>
        {submitted && <span>· {submitted}</span>}
      </div>
      <PrimaryText item={item} />
      <div className="flex items-center gap-1.5 text-xs">
        <StatusIcon className={cn('size-3.5 shrink-0', tone)} />
        <span className={cn('font-medium', tone)} data-testid="request-status">{label}</span>
        {resolved && <span className="text-muted-foreground">· {resolved}</span>}
      </div>
    </li>
  )
}
